import React, { useCallback, useState } from 'react';
import { ISortableTableItem } from './types';
import { DataModelerCdwTableGrid } from '../DataModelerCdwTableGrid';
import DataModelerJoin from '../DataModelerJoin';
import {
  DataModelerTabPanel,
  TableExpandedBackWrapper,
  TableExpandedHeader,
  TableExpandedTitle,
  UnderTableFormContainer,
} from '../DataModelerCommonUI';
import DataModelerRequiredFactFields from '../DataModelerRequiredFields/DataModelerRequiredFactFields';
import DataModelerOptionalDimensionFields from '../DataModelerRequiredFields/DataModelerOptionalDimensionFields';
import { SvgIcon } from '../../../../components/Icon';
import SearchInput from '../../../../components/Form/SearchInput';
import {
  ISchemaDataModelFieldsFragment,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import DataModelerCdwTablePreviewGrid from '../DataModelerCdwTablePreviewGrid/DataModelerCdwTablePreviewGrid';
import useEditDerivedColumnModel from '../DataModelerDerivedColumn/useEditDerivedColumnModel';
import DerivedColumnDraft from '../DataModelerDerivedColumn/DerivedColumnDraft';
import { useAppSelector } from '../../../../redux/hooks';
import { dataModelerSelectCurrentUiState } from '../../../../redux/selectors/dataModeler';

interface IProps {
  tableItem: ISortableTableItem;
  isEditMode: boolean;
  collapse: () => void;
  schemaObj: ISchemaDataModelFieldsFragment;
  searchQuery: string;
  setSearchQuery: (s: string) => void;
  serverError: string | undefined;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
}

const DataModelerTableExpandedContents = ({
  tableItem,
  isEditMode,
  collapse,
  schemaObj,
  searchQuery,
  setSearchQuery,
  serverError,
  availableColumnsFromServer,
}: IProps) => {
  const { tableKey, viewKey, sqlTableName, isFactTable } = tableItem;

  const [derivedColumnIsCollapsed, setDerivedColumnIsCollapsed] =
    useState(false);

  const editDerivedColumnModel = useEditDerivedColumnModel({
    viewKey,
    tableKey,
  });

  // XXX a bit of a hack here - to determine if we are showing t "New Derived Column" form
  const hasStateDraftNewColumn = !!useAppSelector(
    dataModelerSelectCurrentUiState,
  )?.newDerivedColumns[tableKey];
  const hasStateDraftEditedColumn =
    !!editDerivedColumnModel.editedDerivedColumn;

  const collapseDerivedColumnIfActive = useCallback(() => {
    if (hasStateDraftNewColumn || hasStateDraftEditedColumn) {
      setDerivedColumnIsCollapsed(true);
    }
  }, [hasStateDraftEditedColumn, hasStateDraftNewColumn]);

  return (
    <DataModelerTabPanel>
      <TableExpandedHeader>
        <TableExpandedBackWrapper onClick={collapse}>
          <SvgIcon
            name="arrow-left-bold"
            height={12}
            width={12}
            customFill="currentColor"
          />
          Return to Table View
        </TableExpandedBackWrapper>
        <TableExpandedTitle>{sqlTableName.toUpperCase()}</TableExpandedTitle>
        <div>
          <SearchInput
            placeholder={'Search'}
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </div>
      </TableExpandedHeader>
      <DataModelerCdwTableGrid
        searchQuery={searchQuery}
        isEditMode={isEditMode}
        viewKey={viewKey}
        tableKey={tableKey}
        serverError={serverError}
        availableColumnsFromServer={availableColumnsFromServer}
        editDerivedColumnModel={editDerivedColumnModel}
      />
      {isEditMode && (
        <DerivedColumnDraft
          key={`custom-column-${tableKey}`}
          viewKey={viewKey}
          tableKey={tableKey}
          availableColumnsFromServer={availableColumnsFromServer}
          editDerivedColumnModel={editDerivedColumnModel}
          collapsed={derivedColumnIsCollapsed}
          setCollapsed={setDerivedColumnIsCollapsed}
        />
      )}
      <DataModelerCdwTablePreviewGrid
        schemaObj={schemaObj}
        tableItem={tableItem}
        loadColumnsError={serverError}
        availableColumnsFromServer={availableColumnsFromServer}
        collapseDerivedColumn={collapseDerivedColumnIfActive}
      />
      <UnderTableFormContainer>
        {!isFactTable && !serverError && (
          <DataModelerJoin
            schemaObj={schemaObj}
            isEditMode={isEditMode}
            viewKey={viewKey}
            tableKey={tableKey}
            availableColumnsFromServer={availableColumnsFromServer}
          />
        )}
        {isFactTable && !serverError && (
          <DataModelerRequiredFactFields
            schemaObj={schemaObj}
            isEditMode={isEditMode}
            tableKey={tableKey}
            availableColumnsFromServer={availableColumnsFromServer}
          />
        )}
        {!isFactTable && !serverError && (
          <DataModelerOptionalDimensionFields
            schemaObj={schemaObj}
            isEditMode={isEditMode}
            tableKey={tableKey}
            viewKey={viewKey}
            availableColumnsFromServer={availableColumnsFromServer}
          />
        )}
      </UnderTableFormContainer>
    </DataModelerTabPanel>
  );
};

export default DataModelerTableExpandedContents;
